'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface MfaVerifyProps {
  factorId: string;
  accessToken?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function MfaVerify({ factorId, accessToken, onSuccess, onCancel }: MfaVerifyProps) {
  const supabase = createClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // POST to the server-side API route which uses the Bearer token to
      // authenticate a fresh Supabase client, then runs challenge + verify.
      // This avoids the browser-client setSession(empty refresh_token) bug
      // that caused all challenge calls to fail with "Invalid code".
      const res = await fetch('/api/security/mfa-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ factorId, code }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Invalid code. Please try again.');
      }

      // The API route returns the upgraded session (AAL2).
      // Apply it to the browser client so subsequent requests are authenticated.
      if (data.session?.access_token && data.session?.refresh_token) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      // Use a full-page navigation so the browser sends the newly-set auth
      // cookies to the middleware. A client-side router.push() can race with
      // cookie propagation and cause the middleware to see no session,
      // redirecting back to the landing page instead of the dashboard.
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err?.message || 'Invalid code. Please check your authenticator app and try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-blue/20 border border-blue/40 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-white">Two-Factor Authentication</h3>
        <p className="text-silver text-sm mt-1">Enter the 6-digit code from your authenticator app to continue.</p>
      </div>

      <form onSubmit={handleVerify} className="flex flex-col gap-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-silver mb-1.5">
            Authenticator Code
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.6em] font-mono placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all"
            autoComplete="one-time-code"
            autoFocus
            required
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-alert/10 border border-red-alert/30">
            <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-red-alert text-xs">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="btn-electric w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Verifying…
            </>
          ) : (
            'Verify Code'
          )}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-silver hover:text-white transition-colors text-center"
        >
          Sign in with a different account
        </button>
      </form>
    </div>
  );
}
